import {
  isOneOf,
  ROLE_CAPABILITIES,
  VALID_ROLES,
} from '@tronche2tek/shared-types';
import { useTranslation } from 'react-i18next';

import { Card } from '../common/Card';

/**
 * What this person's role lets them do, as a card.
 *
 * It was a disclosure in the page header, sitting between the person's UPN and
 * the first card — nineteen boxes for an admin, one click away from every
 * reader who wanted the identity line above it. The list is a property of the
 * role, not of the person: identical for every admin, and unchanging until
 * `ROLE_CAPABILITIES` itself changes. That is reference material, which belongs
 * where a reader goes to look something up rather than in the way of the page.
 *
 * No fetch: the mapping is compiled into `@tronche2tek/shared-types` and shared
 * with the backend guard, so what is drawn here is the same table that decides
 * the answer server-side.
 *
 * The page renders it for admins only — on their own page as much as on
 * somebody else's. It is the table an admin consults before changing a role,
 * and nobody else has a use for it.
 */
export function UserCapabilitiesCard({
  num,
  role,
}: {
  num: number;
  /** The role of the person the page is about. Admins only see this card. */
  role: string;
}) {
  const { t } = useTranslation();
  const caps = isOneOf(VALID_ROLES, role) ? ROLE_CAPABILITIES[role] : [];

  if (caps.length === 0) {
    return null;
  }

  return (
    <Card
      num={num}
      title={t('profile.identity.capabilities')}
      meta={t(`profile.identity.roles.${role}`, { defaultValue: role })}
    >
      {/* Columns from the card's own width: the page puts it in a grid cell
          whose width has nothing to do with the window's. */}
      <div className="@container">
        <div className="grid grid-cols-1 gap-2 @md:grid-cols-2 @xl:grid-cols-3">
          {caps.map((cap) => (
            <span key={cap} className="epi-tag justify-start" title={cap}>
              {t(`profile.identity.caps.${cap}`, { defaultValue: cap })}
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
}
